import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';

import ChevronDownIcon from '@/assets/svg/chevron-down.svg?react';
import { Spinner } from '@/components/loaders';
import { cn } from '@/lib/utils';

import { pickerTriggerIconSizeClasses } from '../pickerPrimitiveStyles';
import { useComboboxContext } from './ComboboxContext';

const comboboxControlSizeClasses = {
  small: cn('rounded-sm py-px text-sm tracking-[-0.5px]'),
  medium: cn('rounded-md py-1.25 text-sm tracking-[-0.5px]'),
  large: cn('rounded-md py-1.75 text-base tracking-[-0.5px]'),
};

export type ComboboxTriggerProps = ComboboxPrimitive.Trigger.Props & {
  'aria-warning'?: boolean;
};

const ComboboxTrigger = ({
  className,
  'aria-warning': warning,
  'aria-invalid': ariaInvalidFromProps,
  children,
  ...props
}: ComboboxTriggerProps) => {
  const { disabled, size, isLoading } = useComboboxContext();
  const ariaInvalid = ariaInvalidFromProps;

  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      data-size={size}
      data-warning={warning ? 'true' : undefined}
      data-loading={isLoading ? 'true' : undefined}
      disabled={disabled}
      className={cn(
        'flex w-full cursor-pointer items-center justify-between gap-1.5 border bg-white pr-2 pl-3 text-sm whitespace-nowrap transition-colors outline-none select-none',
        'border-gray-600 bg-white text-gray-900 ring-offset-0 data-placeholder:text-gray-500',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',

        'aria-expanded:border-gray-900',
        'aria-expanded:[&_svg]:rotate-180',

        'hover:border-gray-900',

        'focus:border-green-500 focus:ring-1 focus:ring-green-500/50',

        'aria-invalid:border-red-400',
        'aria-invalid:focus:border-red-500 aria-invalid:focus:ring-red-500/50',
        'aria-invalid:hover:border-red-500',

        'data-warning:border-yellow-400 data-warning:hover:border-yellow-500 data-warning:focus:border-yellow-500 data-warning:focus:ring-yellow-500/50',

        'disabled:cursor-not-allowed disabled:border-gray-400 disabled:bg-gray-100 disabled:text-gray-500 disabled:[&_svg]:text-gray-500',

        comboboxControlSizeClasses[size],
        pickerTriggerIconSizeClasses[size],
        className,
      )}
      {...props}
      aria-invalid={ariaInvalid}
    >
      <span data-slot="combobox-value" className="min-w-0 flex-1 truncate text-left">
        {children}
      </span>
      {isLoading ? (
        <Spinner className="pointer-events-none" />
      ) : (
        <ChevronDownIcon className="pointer-events-none text-gray-900 transition-transform duration-100" />
      )}
    </ComboboxPrimitive.Trigger>
  );
};

export default ComboboxTrigger;
